// ============================================
// pages/gunny-bags/GunnyInventoryReport.tsx
// Stock-on-hand report computed from gunny_utilization rollups.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { Warehouse, Loader2, Download, XCircle } from 'lucide-react';
import { seasonsAPI, districtsAPI, gunnySuppliersAPI, gunnyUtilizationAPI } from '../../api/services';
import { num } from '../../types/markfed';
import type { Season, District } from '../../types/markfed';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

type GroupBy = 'supplier' | 'district' | 'society';

interface Row {
  key: string;
  label: string;
  sublabel?: string;
  supplied: number;
  utilized: number;
  returned: number;
  balance: number;
  recordCount: number;
}

export const GunnyInventoryReport: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [districts, setDistricts] = useState<District[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [utilization, setUtilization] = useState<any[]>([]);
  const [districtFilter, setDistrictFilter] = useState<number>(0);
  const [supplierFilter, setSupplierFilter] = useState<number>(0);
  const [groupBy, setGroupBy] = useState<GroupBy>('supplier');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, d, sup] = await Promise.all([seasonsAPI.list(), districtsAPI.list(), gunnySuppliersAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setDistricts(d.data);
        setSuppliers(sup.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (districtFilter) params.district_id = districtFilter;
        const res = await gunnyUtilizationAPI.list(selectedSeasonId, params);
        let rows = Array.isArray(res.data) ? res.data : [];
        if (supplierFilter) rows = rows.filter((r: any) => r.supplier_id === supplierFilter);
        setUtilization(rows);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load utilization');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId, districtFilter, supplierFilter]);

  const districtMap = useMemo(() => {
    const m: Record<number, string> = {};
    districts.forEach((d) => { m[d.id] = d.name; });
    return m;
  }, [districts]);

  const supplierMap = useMemo(() => {
    const m: Record<number, string> = {};
    suppliers.forEach((s) => { m[s.id] = s.supplier_name; });
    return m;
  }, [suppliers]);

  const grouped = useMemo((): Row[] => {
    const map: Record<string, Row> = {};
    for (const u of utilization) {
      let key: string, label: string, sublabel: string | undefined;
      if (groupBy === 'supplier') {
        key = String(u.supplier_id || 0);
        label = u.supplier?.supplier_name || supplierMap[u.supplier_id] || 'Unknown';
        sublabel = u.supplier?.tender_id || '';
      } else if (groupBy === 'district') {
        key = String(u.district_id || 0);
        label = u.district?.name || districtMap[u.district_id] || 'Unknown';
      } else {
        key = `${u.supplier_id}|${u.society_name || '—'}`;
        label = u.society_name || '—';
        sublabel = u.supplier?.supplier_name || supplierMap[u.supplier_id] || '';
      }
      if (!map[key]) {
        map[key] = { key, label, sublabel, supplied: 0, utilized: 0, returned: 0, balance: 0, recordCount: 0 };
      }
      map[key].supplied += num(u.bags_supplied);
      map[key].utilized += num(u.bags_utilized);
      map[key].returned += num(u.bags_returned);
      map[key].recordCount++;
    }
    return Object.values(map).map((r) => ({
      ...r,
      balance: Math.max(r.supplied - r.utilized - r.returned, 0),
    })).sort((a, b) => b.supplied - a.supplied);
  }, [utilization, groupBy, districtMap, supplierMap]);

  const totals = useMemo(() => ({
    supplied: grouped.reduce((s, r) => s + r.supplied, 0),
    utilized: grouped.reduce((s, r) => s + r.utilized, 0),
    returned: grouped.reduce((s, r) => s + r.returned, 0),
    balance: grouped.reduce((s, r) => s + r.balance, 0),
    records: grouped.reduce((s, r) => s + r.recordCount, 0),
  }), [grouped]);

  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(grouped, 25);

  const handlePrint = () => window.print();

  if (loading && utilization.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto print:max-w-none">
      <div className="mb-6 flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Warehouse className="w-7 h-7 text-amber-600" />
            Gunny Bag Inventory
          </h1>
          <p className="text-sm text-gray-500 mt-1">Stock-on-hand computed from utilization records (supplied − utilized − returned)</p>
        </div>
        <button onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white font-semibold rounded-lg text-sm">
          <Download className="w-4 h-4" />Print
        </button>
      </div>

      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4 print:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
            <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">District</label>
            <select value={districtFilter} onChange={(e) => setDistrictFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All</option>
              {districts.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Supplier</label>
            <select value={supplierFilter} onChange={(e) => setSupplierFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All</option>
              {suppliers.map((s) => (<option key={s.id} value={s.id}>{s.supplier_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Group By</label>
            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="supplier">Supplier</option>
              <option value="district">District</option>
              <option value="society">Society</option>
            </select>
          </div>
        </div>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-center gap-2"><XCircle className="w-4 h-4" />{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{groupBy === 'supplier' ? 'Supplier' : groupBy === 'district' ? 'District' : 'Society'}</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Records</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Supplied</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Utilized</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Returned</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">On Hand</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">% Used</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {grouped.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">No utilization records for the selected filters.</td></tr>
              ) : pagedItems.map((r, i) => {
                const pctUsed = r.supplied > 0 ? (r.utilized / r.supplied) * 100 : 0;
                return (
                  <tr key={r.key} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs text-gray-500">{(page - 1) * pageSize + i + 1}</td>
                    <td className="px-3 py-2 text-sm">
                      <div className="font-medium text-gray-900">{r.label}</div>
                      {r.sublabel && <div className="text-[10px] text-gray-400">{r.sublabel}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs text-right text-gray-500">{r.recordCount}</td>
                    <td className="px-3 py-2 text-sm text-right text-blue-700 font-medium">{r.supplied.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-sm text-right text-green-700 font-medium">{r.utilized.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-sm text-right text-yellow-700">{r.returned.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-sm text-right text-purple-700 font-bold">{r.balance.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-sm text-right text-gray-600">{pctUsed.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
            {grouped.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr className="font-bold">
                  <td className="px-3 py-2 text-xs text-gray-700"></td>
                  <td className="px-3 py-2 text-sm text-gray-700">TOTAL</td>
                  <td className="px-3 py-2 text-xs text-right">{totals.records}</td>
                  <td className="px-3 py-2 text-sm text-right text-blue-700">{totals.supplied.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-sm text-right text-green-700">{totals.utilized.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-sm text-right text-yellow-700">{totals.returned.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-sm text-right text-purple-700">{totals.balance.toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-sm text-right text-gray-600">
                    {totals.supplied > 0 ? ((totals.utilized / totals.supplied) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {grouped.length > 0 && (
          <div className="px-4 py-3 border-t bg-gray-50 print:hidden">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="rows"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GunnyInventoryReport;
